import { useState, useEffect } from "react";
import {
  Box,
  Typography,
  IconButton,
  Popover,
  List,
  ListItem,
  Badge,
  alpha,
  useTheme,
  CircularProgress,
  Button,
  Stack,
  Divider,
  Chip,
} from "@mui/material";
import {
  NotificationsNoneOutlined,
  WarningOutlined,
  LocalShippingOutlined,
  CampaignOutlined,
  InfoOutlined,
} from "@mui/icons-material";
import notificationsService from "../services/notificationsService";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router";
import {
  NotificationType,
  PRIORITY_OPTIONS,
} from "../constants/notificationType";
import { WsEvents } from "../constants/wsEvents";
import { AppRoutes } from "../constants/appRoutes";
import { AppConfig } from "../constants/appConfig";
import { logger } from "../utils/logger";

export const NotificationCenter = () => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const navigate = useNavigate();
  const { isAuthenticated, user, accessToken } = useAuth();

  const fetchNotifications = async () => {
    const userId = user?.id || user?.uid;
    if (!userId) return;
    try {
      setLoading(true);
      const response = await notificationsService.getMyNotifications({
        limit: 10,
      });
      const list =
        response?.notifications ||
        response?.data ||
        (Array.isArray(response) ? response : []);
      setNotifications(list);
      setUnreadCount(list.filter((n) => !n.isRead).length);
    } catch (err) {
      console.error("[Notifications] Fetch failed:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let ws = null;
    let reconnectTimeout = null;
    let pollInterval = null;
    let reconnectAttempts = 0;
    let isEffectActive = true;

    const connectWebSocket = () => {
      if (!isAuthenticated || !isEffectActive) return;

      const wsProtocol = window.location.protocol === "https:" ? "wss:" : "ws:";
      const wsUrl =
        AppConfig.ENV.API_URL?.replace(/^https?:/, wsProtocol) +
        `/notifications/ws?project=jigenpass-bo${accessToken ? `&token=${accessToken}` : ""}`;

      logger.debug(
        `[WebSocket] Connecting to real-time notification service...`,
      );
      ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        if (!isEffectActive) {
          ws.close();
          return;
        }
        logger.debug("[WebSocket] Connected to real-time notification service");
        reconnectAttempts = 0;
      };

      ws.onmessage = (event) => {
        if (!isEffectActive) return;
        try {
          const payload = JSON.parse(event.data);
          if (payload.type === WsEvents.PING) {
            ws.send(JSON.stringify({ type: "pong" }));
          } else if (payload.type === WsEvents.NOTIFICATION) {
            setNotifications((prev) => [payload.data, ...prev].slice(0, 10));
            setUnreadCount((prev) => prev + 1);
          }
        } catch (e) {
          logger.error("[WebSocket] Failed to parse payload", {
            error: e.message,
          });
        }
      };

      ws.onerror = () => {
        logger.warn("[WebSocket] Connection error — will retry");
      };

      ws.onclose = (event) => {
        if (!isEffectActive) return;

        const shouldReconnect =
          !event.wasClean ||
          event.code === 1001 ||
          event.code === 1008 ||
          event.code === 1011;

        if (shouldReconnect) {
          const delay = Math.min(1000 * Math.pow(2, reconnectAttempts), 30000);
          logger.debug(
            `[WebSocket] Closed (code ${event.code}). Retrying in ${delay / 1000}s...`,
          );
          reconnectTimeout = setTimeout(() => {
            reconnectAttempts++;
            connectWebSocket();
          }, delay);
        } else {
          logger.debug("[WebSocket] Connection closed cleanly");
        }
      };
    };

    if (isAuthenticated) {
      fetchNotifications();
      connectWebSocket();
      pollInterval = setInterval(fetchNotifications, 120000);
    }

    return () => {
      isEffectActive = false;
      if (pollInterval) clearInterval(pollInterval);
      if (reconnectTimeout) clearTimeout(reconnectTimeout);
      if (ws) ws.close();
    };
  }, [isAuthenticated]);

  const handleNotificationClick = async (n) => {
    if (!n.isRead) {
      try {
        await notificationsService.markAsRead(n.id);
      } catch (err) {
        console.warn("Failed to mark notification as read:", err.message);
      }
      fetchNotifications();
    }

    setAnchorEl(null);
    const data = n.data || {};

    if (data.slipId || n.title.toLowerCase().includes("payment")) {
      navigate(AppRoutes.B2B_PAYMENT_SLIPS);
    } else if (
      data.invoiceNumber ||
      n.title.toLowerCase().includes("invoice")
    ) {
      navigate(AppRoutes.B2B_BILLING);
    } else if (data.quoteNumber || n.title.toLowerCase().includes("quote")) {
      navigate(AppRoutes.B2B_QUOTES);
    } else if (
      data.trackingNumber ||
      n.title.toLowerCase().includes("shipment")
    ) {
      navigate(AppRoutes.B2B_LOGISTICS);
    } else if (data.orderNumber || data.orderId) {
      navigate(AppRoutes.B2B_ORDERS);
    } else if (data.rmaId || n.title.toLowerCase().includes("rma")) {
      navigate(AppRoutes.RMA_DESK);
    }
  };

  return (
    <>
      <IconButton
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{
          color: "text.secondary",
          bgcolor: alpha(theme.palette.divider, 0.05),
          "&:hover": { bgcolor: alpha(theme.palette.divider, 0.1) },
        }}
      >
        <Badge badgeContent={unreadCount} color="error" overlap="circular">
          <NotificationsNoneOutlined />
        </Badge>
      </IconButton>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          elevation: 4,
          sx: {
            mt: 1.5,
            width: 360,
            borderRadius: 2,
            overflow: "hidden",
            border: `1px solid ${theme.palette.divider}`,
          },
        }}
      >
        <Box
          sx={{
            p: 2,
            bgcolor: "background.default",
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="subtitle2" fontWeight={700}>
              System Notifications
            </Typography>
            {unreadCount > 0 && (
              <Button
                size="small"
                onClick={() =>
                  notificationsService.markAllAsRead().then(fetchNotifications)
                }
              >
                Clear All
              </Button>
            )}
          </Stack>
        </Box>

        <Box sx={{ maxHeight: 400, overflowY: "auto" }}>
          {loading && notifications.length === 0 ? (
            <Box sx={{ p: 4, textAlign: "center" }}>
              <CircularProgress size={20} />
            </Box>
          ) : notifications.length === 0 ? (
            <Box sx={{ p: 4, textAlign: "center" }}>
              <Typography variant="body2" color="text.secondary">
                No new notifications
              </Typography>
            </Box>
          ) : (
            <List disablePadding>
              {notifications.map((n) => (
                <ListItem
                  key={n.id}
                  onClick={() => handleNotificationClick(n)}
                  sx={{
                    cursor: "pointer",
                    p: 2,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    bgcolor: n.isRead
                      ? "transparent"
                      : alpha(theme.palette.primary.main, 0.04),
                    "&:hover": {
                      bgcolor: alpha(theme.palette.action.hover, 0.1),
                    },
                  }}
                >
                  <Stack direction="row" spacing={2}>
                    <Box sx={{ mt: 0.5 }}>
                      {n.type === NotificationType.ALERT ? (
                        <WarningOutlined color="error" fontSize="small" />
                      ) : n.type === NotificationType.ORDER_STATUS ? (
                        <LocalShippingOutlined
                          color="primary"
                          fontSize="small"
                        />
                      ) : n.type === NotificationType.PROMOTION ? (
                        <CampaignOutlined color="info" fontSize="small" />
                      ) : n.type === NotificationType.INFO ? (
                        <InfoOutlined color="info" fontSize="small" />
                      ) : (
                        <NotificationsNoneOutlined
                          color="action"
                          fontSize="small"
                        />
                      )}
                    </Box>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography
                        component="div"
                        variant="body2"
                        fontWeight={n.isRead ? 500 : 700}
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        {n.title}
                        {n.priority && (
                          <Chip
                            label={n.priority.toUpperCase()}
                            size="small"
                            sx={{
                              height: 16,
                              fontSize: "0.65rem",
                              fontWeight: 800,
                              color:
                                PRIORITY_OPTIONS.find(
                                  (p) => p.value === n.priority,
                                )?.color || "text.secondary",
                              bgcolor:
                                PRIORITY_OPTIONS.find(
                                  (p) => p.value === n.priority,
                                )?.bgColor || "action.hover",
                            }}
                          />
                        )}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                      >
                        {n.message}
                      </Typography>
                    </Box>
                  </Stack>
                </ListItem>
              ))}
            </List>
          )}

          <Divider />
          <Box sx={{ p: 1, textAlign: "center" }}>
            <Button
              fullWidth
              size="small"
              onClick={() => {
                setAnchorEl(null);
                navigate(AppRoutes.NOTIFICATIONS);
              }}
              sx={{ fontWeight: 600, borderRadius: 2 }}
            >
              View All Notifications
            </Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
};
